import type { AppState, GameState, VoteEntry } from '@/types'
import { byId, byIdMaybe, showToast } from '@/utils/dom'
import { NumberGrid } from '@/components/NumberGrid'
import { VoteChart } from '@/components/VoteChart'

export type CallNumberCallback = (num: number) => void
export type NavigateCallback = (page: AppState['currentPage']) => void
export type NewGameCallback = () => void
export type CompactToggleCallback = (on: boolean) => void

export class GamePage {
  private grid!: NumberGrid
  private chart!: VoteChart
  // Compact mode has its own grid + chart instances
  private cvGrid!: NumberGrid
  private cvChart!: VoteChart

  private onCall: CallNumberCallback
  private onNavigate: NavigateCallback
  private onNewGame: NewGameCallback
  private onCompactToggle: CompactToggleCallback

  constructor(
    onCall: CallNumberCallback,
    onNavigate: NavigateCallback,
    onNewGame: NewGameCallback,
    onCompactToggle: CompactToggleCallback,
  ) {
    this.onCall = onCall
    this.onNavigate = onNavigate
    this.onNewGame = onNewGame
    this.onCompactToggle = onCompactToggle
  }

  mount(gameState: GameState): void {
    // Normal grid
    const gridEl = byId('number-grid')
    this.grid = new NumberGrid(gridEl, (num) => this.onCall(num))
    this.grid.mount(gameState)

    // Normal chart
    const chartEl = byId('vote-chart')
    this.chart = new VoteChart()
    this.chart.init(chartEl)

    // Compact grid
    const cvGridEl = byId('cv-number-grid')
    this.cvGrid = new NumberGrid(cvGridEl, (num) => this.onCall(num))
    this.cvGrid.mount(gameState)

    // Compact chart
    const cvChartEl = byId('cv-vote-chart')
    this.cvChart = new VoteChart()
    this.cvChart.init(cvChartEl)

    this.bindEvents()
  }

  // ─── Full update ─────────────────────────────────────────────

  updateAll(state: AppState): void {
    this.updateRange(state.game.rangeMin, state.game.rangeMax)
    this.updateCalledCount(state.game.calledNumbers.size)
    this.updateGrid(state.game)
    this.updateStatusBar(state.game)
    this.updateCallBtn(state.game)
    this.updateHistory(state.game)
    this.updateWsStatus(state.wsConnected, state.game.isRunning)
    this.updateNewGameBtn(state.game)
  }

  // ─── Partial updates ─────────────────────────────────────────

  updateRange(min: number, max: number): void {
    const setId = (id: string, v: string) => { const e = byIdMaybe(id); if (e) e.textContent = v }
    setId('range-min', String(min)); setId('cv-range-min', String(min))
    setId('range-max', String(max)); setId('cv-range-max', String(max))
  }

  updateCalledCount(count: number): void {
    const setId = (id: string, v: string) => { const e = byIdMaybe(id); if (e) e.textContent = v }
    setId('called-count', String(count))
    setId('cv-called-count', String(count))
  }

  updateGrid(gameState: GameState): void {
    this.grid?.patch(gameState)
    this.cvGrid?.patch(gameState)
  }

  updateVotes(top3: VoteEntry[]): void {
    console.log(top3);
    
    this.chart?.update(top3)
    this.cvChart?.update(top3)
    this.chart?.resize()
    this.cvChart?.resize()
  }

  updateWsStatus(connected: boolean, isRunning: boolean): void {
    const setDot = (id: string) => byIdMaybe(id)?.classList.toggle('ws-dot--connected', connected)
    const setLabel = (id: string, t: string) => { const e = byIdMaybe(id); if (e) e.textContent = t }
    const label = connected ? '弹幕接收中' : '未连接'

    setDot('ws-dot'); setDot('cv-ws-dot')
    setLabel('ws-label', label); setLabel('cv-ws-label', label)

    const banner = byIdMaybe('demo-banner')
    const cvBanner = byIdMaybe('cv-demo-banner')
    banner?.classList.toggle('demo-banner--show', !connected && isRunning)
    cvBanner?.classList.toggle('demo-banner--show', !connected && isRunning)
  }

  updateStatusBar(game: GameState): void {
    const setText = (id: string, text: string, danger: boolean) => {
      const el = byIdMaybe(id)
      if (!el) return
      el.textContent = text
      el.className = id.includes('cv') ? `cv-status${danger ? ' cv-status--danger' : ''}` : `status-text${danger ? ' status-text--danger' : ''}`
    }

    if (!game.isRunning && !game.isOver) {
      setText('status-text', '⏸ 等待开始 — 点击「开始新一局」', false)
      setText('cv-status-text', '⏸ 等待开始', false)
    } else if (game.isOver) {
      setText('status-text', `💥 游戏结束！炸弹是 ${game.bomb}`, true)
      setText('cv-status-text', `💥 炸弹是 ${game.bomb}！`, true)
    } else {
      setText('status-text', `🎮 游戏进行中 · 范围 ${game.rangeMin}~${game.rangeMax}`, false)
      setText('cv-status-text', `🎮 ${game.rangeMin}~${game.rangeMax}`, false)
    }
  }

  updateCallBtn(game: GameState): void {
    const disable = !game.isRunning || game.isOver
    const btn = byIdMaybe<HTMLButtonElement>('btn-call-number')
    const cvBtn = byIdMaybe<HTMLButtonElement>('cv-btn-call')
    if (btn) btn.disabled = disable
    if (cvBtn) cvBtn.disabled = disable
  }

  updateNewGameBtn(game: GameState): void {
    const label = game.isRunning && !game.isOver ? '🔁 重置本局' : '▶ 开始新一局'
    const btn = byIdMaybe('btn-new-game')
    const cvBtn = byIdMaybe('cv-btn-new')
    if (btn) btn.textContent = label
    if (cvBtn) cvBtn.textContent = game.isRunning && !game.isOver ? '🔁 重置' : '▶ 新一局'
  }

  updateHistory(game: GameState): void {
    const list = byIdMaybe('history-list')
    if (!list) return
    const html = game.history.slice().reverse().map(
      (h) => `<div class="history-item ${h.isBomb ? 'history-item--bomb' : ''}">${h.message}</div>`,
    ).join('')
    list.innerHTML = html
    list.scrollTop = 0
  }

  showCallError(msg: string): void {
    showToast(msg)
    ;['call-input', 'cv-call-input'].forEach(id => {
      const input = byIdMaybe<HTMLInputElement>(id)
      input?.classList.add('input--error')
      setTimeout(() => input?.classList.remove('input--error'), 600)
    })
  }

  setCompactMode(on: boolean): void {
    const normal = byIdMaybe('normal-view')
    const compact = byIdMaybe('compact-view')
    if (normal) normal.style.display = on ? 'none' : 'flex'
    if (compact) compact.style.display = on ? 'flex' : 'none'
    // Resize both charts after layout change
    setTimeout(() => {
      this.chart?.resize()
      this.cvChart?.resize()
    }, 60)
  }

  // ─── Events ─────────────────────────────────────────────────

  private bindEvents(): void {
    byId('btn-new-game').addEventListener('click', () => this.onNewGame())
    byId('btn-game-to-home').addEventListener('click', () => this.onNavigate('home'))
    byId('btn-game-to-about').addEventListener('click', () => this.onNavigate('about'))
    byId('btn-call-number').addEventListener('click', () => this.submitInput('call-input'))
    byId('call-input').addEventListener('keydown', (e) => { if ((e as KeyboardEvent).key === 'Enter') this.submitInput('call-input') })

    // Compact mode toggle
    byId('btn-compact-on').addEventListener('click', () => this.onCompactToggle(true))
    byId('btn-compact-off').addEventListener('click', () => this.onCompactToggle(false))

    // Compact actions
    byId('cv-btn-new').addEventListener('click', () => this.onNewGame())
    byId('cv-btn-call').addEventListener('click', () => this.submitInput('cv-call-input'))
    byId('cv-call-input').addEventListener('keydown', (e) => { if ((e as KeyboardEvent).key === 'Enter') this.submitInput('cv-call-input') })
  }

  private submitInput(inputId: string): void {
    const input = byIdMaybe<HTMLInputElement>(inputId)
    if (!input) return
    const val = parseInt(input.value, 10)
    if (!isNaN(val)) {
      this.onCall(val)
      input.value = ''
    } else {
      showToast('请输入有效数字')
    }
  }
}
